import {DeliverySection as DeliverySectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {useFormatDateWithShortWeekName, useTranslation} from '@aboutyou/i18n';
import {BasketSection, DeliverySection} from '@aboutyou/ui-components';
import {isCaseTypeCheck} from '@aboutyou/utils';
import React, {useCallback, useRef, useState} from 'react';
import styled from 'styled-components';
import {useActivatePreselectedBestCouponABTest} from '../../../features/Coupons/useActivatePreselectedBestCouponABTest';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {useProductImageType} from '../../../shared-deprecated/useProductImageType';
import {MerchantLegalInfo} from '../../MerchantLegalInfo/MerchantLegalInfo';
import {useShouldCheckForPriceRise} from '../hooks/useShouldCheckForPriceRise';
import {BasketProductCard} from './Components/BasketProductCard';
import {DeliveriesProps} from './Deliveries';
import {useShowDeliveriesAsDayDates} from './hooks/useShowDeliveriesAsDayDates';

export const DeliveriesView: React.FC<
  DeliverySectionType & DeliveriesProps
> = ({type, onBasketQuantityChange, onBasketItemRemovedSuccessfully}) => {
  const {t} = useTranslation();
  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();
  const formatDateWithShortWeekName = useFormatDateWithShortWeekName();
  const deliveries = isCaseTypeCheck('delivery', type)
    ? type.delivery.deliveries
    : [];

  const deliveriesWithDay = useShowDeliveriesAsDayDates(deliveries);
  const shouldCheckForPriceRise = useShouldCheckForPriceRise(deliveries);

  const [productImageType] = useProductImageType();
  const [legalInfoDetails, setLegalInfoDetails] = useState<
    | {
        size: number;
        productId: number;
      }
    | undefined
  >(undefined);

  const handleLegalInfoClick = (options: {size: number; productId: number}) => {
    setLegalInfoDetails(options);
  };

  useActivatePreselectedBestCouponABTest();

  const listContainerRef = useRef<HTMLDivElement>(null);

  const handleItemRemoved = useCallback(() => {
    // Move focus to the stable container
    listContainerRef.current?.focus();
    onBasketItemRemovedSuccessfully?.();
  }, [onBasketItemRemovedSuccessfully]);

  // Always rendered (even with nothing to show) so the focus target in
  // handleItemRemoved above survives deleting the very last item
  return (
    <StyledBasketSection
      ref={listContainerRef}
      tabIndex={-1}
      role="group"
      aria-label={t('Basket items')}
      data-testid="deliveriesListContainer"
    >
      {deliveriesWithDay.map((data, index) => {
        const deliveryDate = `${formatDateWithShortWeekName(
          data.deliveryPeriod.startDate,
        )} - ${formatDateWithShortWeekName(data.deliveryPeriod.endDate)}`;

        return (
          <StyledDeliverySection
            headline={data.headline}
            shippedBy={data.shippingMerchant?.shippedBy.text}
            deliveryDate={data.showDateAsDayTitle || deliveryDate}
            hideInfoText={
              disableUrgencyTriggers || index !== deliveriesWithDay.length - 1
            }
            key={index}
            withoutBottomGap={index + 1 !== deliveriesWithDay.length}
            withoutRoundedTopCorners={index > 0}
            withoutRoundedBottomCorners={index + 1 < deliveriesWithDay.length}
            hightlightDateLabel={data.showDatesAsDays}
          >
            {data.items.map(item => {
              return (
                <BasketProductCard
                  key={`${item.product.id}-${item.size.id}`}
                  item={item}
                  preferredProductImageType={productImageType}
                  onBasketQuantityChange={onBasketQuantityChange}
                  onBasketItemRemovedSuccessfully={handleItemRemoved}
                  onLegalInfoClick={handleLegalInfoClick}
                  shouldCheckForPriceRise={shouldCheckForPriceRise}
                />
              );
            })}
          </StyledDeliverySection>
        );
      })}
      {legalInfoDetails && (
        <MerchantLegalInfo
          sizeId={legalInfoDetails.size}
          productId={legalInfoDetails.productId}
          onClose={() => {
            setLegalInfoDetails(undefined);
          }}
        />
      )}
    </StyledBasketSection>
  );
};

const StyledDeliverySection = styled(DeliverySection)`
  &:last-child {
    padding-bottom: 0;
  }
`;

const StyledBasketSection = styled(BasketSection)`
  &:focus {
    outline: none;
  }

  &:empty {
    background: none;
    border-bottom: none;
  }
`;
